import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, Backdrop, CircularProgress, Button, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, TextField, Checkbox, useTheme } from '@mui/material';
import { QueueDateCalendar, parseUtcDateKey } from './QueueDateCalendar';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { apiClient } from 'utils/ApiClient';
import { Logger } from 'utils/Logger/Logger';
import { DynamicBreadcrumbs } from 'components/Breadcrumb';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import { Area, AreaChart, CartesianGrid, Legend, ReferenceLine, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from 'recharts';

interface QueueItem {
  destinationNumber?: string;
  messageBody?: string;
  status?: string;
  createdAt?: string | Date;
  lastUpdated?: string | Date;
  assignedWorkerId?: string;
  assignedWorkerPhoneNumber?: string;
  assignedOperatorName?: string;
  companyId?: string;
  companyName?: string;
  campaignId?: string;
  campaignName?: string;
  executionLogEntryId?: string;
  [key: string]: any;
}

interface WorkerPoolDoc {
  _id: string;
  poolName: string;
  messagingQueue?: QueueItem[];
  [key: string]: any;
}

const CHART_DAYS = 90;

export const MessagingQueue: React.FC = () => {
  const { poolId } = useParams<{ poolId: string }>();
  const theme = useTheme();
  const [loading, setLoading] = useState<boolean>(true);
  const [pool, setPool] = useState<WorkerPoolDoc | null>(null);
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const [deliveredCounts, setDeliveredCounts] = useState<Array<{ date: string; scheduled: number; inProgress: number; success: number; failed: number; skipped: number; completed: number }>>([]);
  const [chartLoading, setChartLoading] = useState<boolean>(true);
  const [viewOpen, setViewOpen] = useState<boolean>(false);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [retryOpen, setRetryOpen] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<QueueItem | null>(null);
  const [search, setSearch] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const d = new Date();
    const dd = String(d.getUTCDate()).padStart(2, '0');
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const yyyy = d.getUTCFullYear();
    return `${dd}-${mm}-${yyyy}`;
  });
  const [items, setItems] = useState<QueueItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const last90DaysOptions = useMemo(() => {
    const arr: string[] = [];
    const today = new Date();
    for (let i = 0; i < 90; i++) {
      const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
      d.setUTCDate(d.getUTCDate() - i);
      const dd = String(d.getUTCDate()).padStart(2, '0');
      const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
      const yyyy = d.getUTCFullYear();
      arr.push(`${dd}-${mm}-${yyyy}`);
    }
    return arr;
  }, []);

  // Build the 90-day chart series (oldest → newest), filling missing days with 0.
  const chartData = useMemo(() => {
    const byDate = new Map(deliveredCounts.map((c) => [c.date, c]));
    return [...last90DaysOptions].reverse().map((d) => {
      const row = byDate.get(d);
      return {
        date: d,                 // DD-MM-YYYY (matches selectedDate + dropdown values)
        label: d.slice(0, 5),    // DD-MM for the axis tick
        scheduled: row?.scheduled || 0,
        inProgress: row?.inProgress || 0,
        success: row?.success || 0,
        failed: row?.failed || 0,
        skipped: row?.skipped || 0,
        completed: row?.completed || 0
      };
    });
  }, [deliveredCounts, last90DaysOptions]);

  const availableDates = useMemo(() => {
    const dates = new Set<string>();
    for (const row of deliveredCounts) {
      const total = (row.scheduled || 0) + (row.inProgress || 0) + (row.success || 0)
        + (row.failed || 0) + (row.skipped || 0) + (row.completed || 0);
      if (total > 0 && row.date) dates.add(row.date);
    }
    return dates;
  }, [deliveredCounts]);

  // If the open day has no messages, select the newest day that does.
  useEffect(() => {
    if (chartLoading || availableDates.size === 0 || availableDates.has(selectedDate)) return;
    const newest = [...availableDates].sort((a, b) => {
      const aTime = parseUtcDateKey(a)?.getTime() ?? 0;
      const bTime = parseUtcDateKey(b)?.getTime() ?? 0;
      return bTime - aTime;
    })[0];
    if (newest) setSelectedDate(newest);
  }, [chartLoading, availableDates, selectedDate]);

  // One area per message status; colors mirror the status chips below the chart.
  const statusSeries = useMemo(() => ([
    { key: 'scheduled', label: 'Scheduled', color: theme.palette.warning.main },
    { key: 'inProgress', label: 'In progress', color: theme.palette.info.main },
    { key: 'success', label: 'Success', color: theme.palette.success.main },
    { key: 'failed', label: 'Failed', color: theme.palette.error.main },
    { key: 'skipped', label: 'Skipped', color: theme.palette.grey[500] },
    { key: 'completed', label: 'Completed', color: theme.palette.primary.main }
  ]), [theme]);

  const handleChartClick = useCallback((state: any) => {
    const label = state?.activeLabel;
    if (typeof label === 'string' && availableDates.has(label)) {
      setSelectedDate(label);
    }
  }, [availableDates]);

  useEffect(() => {
    const fetchPool = async () => {
      if (!poolId) return;
      try {
        setLoading(true);
        const resp = await apiClient.get(`/iu/workerpool/${poolId}`);
        const data = (resp as any)?.data || resp;
        setPool(data);
      } catch (e: any) {
        Logger.error('Settings/WorkerPool/MessagingQueue', 'Failed to fetch worker pool: ' + (e?.message || ''));
      } finally {
        setLoading(false);
      }
    };
    fetchPool();
  }, [poolId, refreshKey]);

  useEffect(() => {
    const fetchDeliveredDaily = async () => {
      if (!poolId) return;
      try {
        setChartLoading(true);
        const resp = await apiClient.get(`/iu/workerpool/${poolId}/messaging-queue-stats/delivered-daily?days=${CHART_DAYS}`);
        const data = (resp as any)?.data || resp;
        const counts = Array.isArray(data?.counts) ? data.counts : [];
        setDeliveredCounts(counts);
      } catch (e: any) {
        Logger.error('Settings/WorkerPool/MessagingQueue', 'Failed to fetch delivered daily counts: ' + (e?.message || ''));
        setDeliveredCounts([]);
      } finally {
        setChartLoading(false);
      }
    };
    fetchDeliveredDaily();
  }, [poolId, refreshKey]);

  useEffect(() => {
    const fetchQueueByDate = async () => {
      if (!poolId || !selectedDate) return;
      try {
        setLoading(true);
        const resp = await apiClient.get(`/iu/workerpool/${poolId}/messaging-queue/${selectedDate}`);
        const data = (resp as any)?.data || resp;
        const arr = Array.isArray(data) ? data : [];
        setItems(arr);
      } catch (e: any) {
        Logger.error('Settings/WorkerPool/MessagingQueue', 'Failed to fetch queue by date: ' + (e?.message || ''));
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    fetchQueueByDate();
  }, [poolId, selectedDate, refreshKey]);

  const breadcrumbItems = useMemo(() => {
    return [
      { itemPath: '/administration/worker-pool', itemLabel: 'WorkerPool' },
      { itemPath: `/administration/worker-pool/${poolId}/messaging-queue`, itemLabel: pool?.poolName || '...' }
    ];
  }, [pool?.poolName, poolId]);

  const filteredItems: QueueItem[] = useMemo(() => {
    const term = (search || '').trim().toLowerCase();
    const base = !term ? items : items.filter(item =>
      String(item.destinationNumber || '').toLowerCase().includes(term)
    );
    return base;
  }, [items, search]);

  const eligibleItemIds = useMemo(() => {
    return filteredItems
      .filter(it => {
        const s = String(it.status || '').toLowerCase();
        return s === 'failed' || s === 'in-progress';
      })
      .map(it => String((it as any)?._id || ''))
      .filter(Boolean);
  }, [filteredItems]);

  const allEligibleSelected = eligibleItemIds.length > 0 && eligibleItemIds.every(id => selectedIds.has(id));
  const someEligibleSelected = eligibleItemIds.some(id => selectedIds.has(id)) && !allEligibleSelected;

  const toggleSelectOne = (id?: string, canSelect?: boolean) => {
    const key = String(id || '');
    if (!key || !canSelect) return;
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSelectAllEligible = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allEligibleSelected) {
        // unselect all eligible
        eligibleItemIds.forEach(id => next.delete(id));
      } else {
        eligibleItemIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const bulkRetrySelected = async () => {
    if (!poolId) return;
    const ids = Array.from(selectedIds).filter(Boolean);
    if (ids.length === 0) return;
    try {
      await apiClient.post(`/iu/workerpool/${poolId}/messaging-queue/retry`, { ids });
      setSelectedIds(new Set());
      setRefreshKey(x => x + 1);
    } catch (e: any) {
      Logger.error('Settings/WorkerPool/MessagingQueue', 'Failed to bulk retry: ' + (e?.message || ''));
    }
  };

  const renderStatusChip = (status?: string) => {
    const val = String(status || '').toLowerCase();
    if (val === 'failed') return <Chip label="Failed" color="error" variant="outlined" size="small" />;
    if (val === 'success') return <Chip label="Success" color="success" variant="outlined" size="small" />;
    if (val === 'in-progress') return <Chip label="In progress" color="info" variant="outlined" size="small" />;
    if (val === 'scheduled') return <Chip label="Scheduled" color="warning" variant="outlined" size="small" />;
    if (val === 'skipped') return <Chip label="Skipped" color="warning" variant="outlined" size="small" />;
    return <Chip label={status || ''} variant="outlined" size="small" />;
  };

  const handleView = (item: QueueItem) => {
    setSelectedItem(item);
    setViewOpen(true);
  };

  const handleDelete = (item: QueueItem) => {
    setSelectedItem(item);
    setDeleteOpen(true);
  };

  const handleRetry = (item: QueueItem) => {
    setSelectedItem(item);
    setRetryOpen(true);
  };

  const confirmDelete = async () => {
    if (!poolId || !selectedItem || !(selectedItem as any)?._id) {
      setDeleteOpen(false);
      return;
    }
    try {
      await apiClient.delete(`/iu/workerpool/${poolId}/messaging-queue/delete/${(selectedItem as any)._id}`);
      setDeleteOpen(false);
      setSelectedItem(null);
      setRefreshKey(x => x + 1);
    } catch (e: any) {
      Logger.error('Settings/WorkerPool/MessagingQueue', 'Failed to delete queue item: ' + (e?.message || ''));
      setDeleteOpen(false);
    }
  };

  const confirmRetry = async () => {
    if (!poolId || !selectedItem || !(selectedItem as any)?._id) {
      setRetryOpen(false);
      return;
    }
    try {
      await apiClient.post(`/iu/workerpool/${poolId}/messaging-queue/retry/${(selectedItem as any)._id}`, {});
      setRetryOpen(false);
      setSelectedItem(null);
      setRefreshKey(x => x + 1);
    } catch (e: any) {
      Logger.error('Settings/WorkerPool/MessagingQueue', 'Failed to retry queue item: ' + (e?.message || ''));
      setRetryOpen(false);
    }
  };

  return (
    <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1} width="100%">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <DynamicBreadcrumbs items={breadcrumbItems} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
          {selectedIds.size > 0 && (
            <Button
              variant="contained"
              color="warning"
              size="small"
              onClick={bulkRetrySelected}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Retry Selected
            </Button>
          )}
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search destination number"
            size="small"
            variant="outlined"
            sx={{ minWidth: { xs: '100%', sm: 260 } }}
          />
          <QueueDateCalendar
            value={selectedDate}
            availableDates={availableDates}
            dayWindow={CHART_DAYS}
            loading={chartLoading}
            onChange={setSelectedDate}
          />
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => setRefreshKey(x => x + 1)}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Typography variant="h5" sx={{ mb: 2 }}>{pool?.poolName || ''} - Messaging Queue</Typography>

      {/* Delivered-messages area chart (last 90 days). Click a date to filter the table below. */}
      <Paper sx={{ borderRadius: '8px', p: 2, mb: 2, width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1, gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Messages by Status (last {CHART_DAYS} days)</Typography>
          <Typography variant="caption" color="text.secondary">
            Click a day with messages to view it · Selected: {selectedDate}
          </Typography>
        </Box>
        {chartLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 260 }}>
            <CircularProgress color="secondary" size={28} />
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart
              data={chartData}
              margin={{ top: 10, right: 16, left: 0, bottom: 0 }}
              onClick={handleChartClick}
              style={{ cursor: 'pointer' }}
            >
              <defs>
                {statusSeries.map((s) => (
                  <linearGradient key={s.key} id={`fill-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={s.color} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={s.color} stopOpacity={0.02} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis
                dataKey="date"
                tickFormatter={(v: string) => String(v).slice(0, 5)}
                interval={Math.floor(CHART_DAYS / 12)}
                minTickGap={16}
                tick={{ fontSize: 11 }}
              />
              <YAxis allowDecimals={false} width={40} tick={{ fontSize: 11 }} />
              <RechartsTooltip labelFormatter={(label: any) => `Date: ${label}`} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {chartData.some((d) => d.date === selectedDate) && (
                <ReferenceLine x={selectedDate} stroke={theme.palette.text.primary} strokeDasharray="4 2" />
              )}
              {statusSeries.map((s) => (
                <Area
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={s.label}
                  stroke={s.color}
                  strokeWidth={2}
                  fill={`url(#fill-${s.key})`}
                  activeDot={{ r: 4 }}
                  isAnimationActive={false}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '256px' }}>
          <Backdrop sx={{ color: 'secondary.main', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
            <CircularProgress color="inherit" />
          </Backdrop>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: '8px' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={someEligibleSelected}
                    checked={allEligibleSelected}
                    onChange={toggleSelectAllEligible}
                    inputProps={{ 'aria-label': 'select all eligible' }}
                  />
                </TableCell>
                <TableCell>Destination Number</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Last Updated</TableCell>
                <TableCell>Assigned Worker</TableCell>
                <TableCell>Operator Name</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Campaign</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">No items in queue.</TableCell>
                </TableRow>
              ) : (
                filteredItems.map((item: QueueItem, idx: number) => {
                  const statusVal = String(item.status || '').toLowerCase();
                  const canDelete = !(statusVal === 'success' || statusVal === 'failed' || statusVal === 'skipped');
                  const canRetry = statusVal === 'failed' || statusVal === 'skipped';
                  const canSelect = statusVal === 'failed' || statusVal === 'in-progress';
                  const rowId = String((item as any)?._id || '');
                  return (
                    <TableRow key={(item as any)?._id || idx}>
                      <TableCell padding="checkbox">
                        {canSelect ? (
                          <Checkbox
                            checked={rowId ? selectedIds.has(rowId) : false}
                            onChange={() => toggleSelectOne(rowId, canSelect)}
                          />
                        ) : null}
                      </TableCell>
                      <TableCell>{item.destinationNumber || ''}</TableCell>
                      <TableCell>{renderStatusChip(item.status)}</TableCell>
                      <TableCell>
                        {item.createdAt ? new Date(item.createdAt).toLocaleString('en-GB') : ''}
                      </TableCell>
                      <TableCell>
                        {item.lastUpdated ? new Date(item.lastUpdated).toLocaleString('en-GB') : ''}
                      </TableCell>
                      <TableCell>{item.assignedWorkerPhoneNumber || ''}</TableCell>
                      <TableCell>{item.assignedOperatorName || ''}</TableCell>
                      <TableCell>{item.companyName || ''}</TableCell>
                      <TableCell>{item.campaignName || ''}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Tooltip title="View">
                            <Button
                              aria-label="view"
                              color="secondary"
                              variant="contained"
                              size="small"
                              onClick={() => handleView(item)}
                              sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </Button>
                          </Tooltip>
                          {canRetry && (
                            <Tooltip title="Retry">
                              <Button
                                aria-label="retry"
                                color="warning"
                                variant="contained"
                                size="small"
                                onClick={() => handleRetry(item)}
                                sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                              >
                                Retry
                              </Button>
                            </Tooltip>
                          )}
                          {canDelete && (
                            <Tooltip title="Delete">
                              <Button
                                aria-label="delete"
                                color="error"
                                variant="contained"
                                size="small"
                                onClick={() => handleDelete(item)}
                                sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                              >
                                <DeleteIcon fontSize="small" />
                              </Button>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {/* View Dialog */}
      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Queue Item Details</DialogTitle>
        <DialogContent dividers>
          {selectedItem ? (
            <Box component="div" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              {Object.keys(selectedItem).map((k) => (
                <Box key={k} sx={{ wordBreak: 'break-word' }}>
                  <Typography variant="caption" color="text.secondary">{k}</Typography>
                  <Typography variant="body2">{String((selectedItem as any)[k])}</Typography>
                </Box>
              ))}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={() => setViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete Message</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Deleting the Message from the queue is not a recoverable step.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Confirm</Button>
        </DialogActions>
      </Dialog>
      {/* Retry Confirmation Dialog */}
      <Dialog open={retryOpen} onClose={() => setRetryOpen(false)}>
        <DialogTitle>Retry Message</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Once confirmed, the worker will retry to send this message by rescheduling it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={() => setRetryOpen(false)}>Cancel</Button>
          <Button onClick={confirmRetry} color="warning" variant="contained">Confirm</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MessagingQueue;


